import axios from "axios";
import Liferay from "./Liferay";

/**
 * Inicializa una instancia de Axios pre-configurada con valores por defecto.
 *
 * @returns Instancia de Axios.
 */
function getAxiosInstance() {

  const instance = axios.create();

  instance.defaults.baseURL = "/o";

  instance.defaults.headers.common.Accept = "application/json";
  instance.defaults.headers.post["Content-Type"] = "application/json";
  instance.defaults.headers["Accept-Language"] = Liferay.ThemeDisplay.getBCP47LanguageId();

  return instance;

}

/**
 * Obtiene token.
 *
 * @returns Token de acceso.
 */
async function getToken() {

  const body = {
    grant_type: "client_credentials",
    client_id: process.env.REACT_APP_DR_NOW_USER_SIGN_UP_APP_OAUTH_CLIENT_ID,
    client_secret: process.env.REACT_APP_DR_NOW_USER_SIGN_UP_APP_OAUTH_CLIENT_SECRET,
  };

  const response = await getAxiosInstance().post(
    Liferay.OAuth2.getTokenURL(),
    body,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );

  return response.data.access_token;

}

/**
 * Obtiene el contenido del modal de términos y condiciones.
 *
 * @returns HTML con término y condiciones.
 */
async function getTermsAndConditionsContent() {

  const contentKey = "DRNOW_TERMS";
  const siteId = Liferay.ThemeDisplay.getScopeGroupId();

  const token = await getToken();

  const url = `/headless-delivery/v1.0/sites/${siteId}/structured-contents/by-key/${contentKey}`;

  const webContentDataResponse = await getAxiosInstance().get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const renderedContentUrl = "/headless-delivery/v1.0/structured-contents/"
    .concat(`${webContentDataResponse.data.id}/rendered-content/DRNOW_TERMS_TEMPLATE`);

  return getAxiosInstance().get(renderedContentUrl, {
    headers: {
      Accept: "text/html,application/xhtml+xml,application/xml",
      Authorization: `Bearer ${token}`,
    },
  });

}

/**
 * Registra al usuario en el Liferay.
 *
 * @param user - Datos del usuario.
 *
 * @returns Respuesta del servicio.
 */
async function createLiferayUser(user) {

  const token = await getToken();

  return getAxiosInstance().post(
    "/headless-admin-user/v1.0/user-accounts",
    user,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

}

/**
 * Crea un drnow User con la información proporcionada.
 *
 * @param user - Información del usuario.
 *
 * @returns El usuario creado.
 */
async function createDrnowUser(user) {

  const token = await getToken();

  return getAxiosInstance().post("c/drnowusers", user, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

}

/**
 * Actualiza los datos del usuario en Liferay.
 *
 * @param userId - Identificador del usuario.
 *
 * @param data - Datos recogidos en el formulario de registro.
 *
 * @returns Respuesta del servicio.
 */
async function updateLiferayUser(userId, data) {

  const token = await getToken();

  return getAxiosInstance().put(
    `/headless-admin-user/v1.0/user-accounts/${userId}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

}

/**
 * Obtiene el catálogo de países.
 *
 * @returns Catálogo de países.
 */
async function getCountriesCatalog() {

  const token = await getToken();

  return getAxiosInstance().get("/headless-admin-address/v1.0/countries", {
    params: {
      sort: "name",
      pageSize: 250,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

}

/**
 * Evalúa si el email ya ha sido registrado en algún usuario.
 *
 * @param email - Email a evaluar.
 *
 * @returns Booleano indicando si el email ha sido registrado.
 */
async function checkEmail(email) {

  const token = await getToken();

  const url = "headless-admin-user/v1.0/user-accounts";

  const response = await getAxiosInstance().get(url, {
    params: {
      filter: `emailAddress eq '${email}'`,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.data.totalCount === 0) {

    return false;

  }

  return true;

}

const client = {
  getTermsAndConditionsContent,
  updateLiferayUser,
  createLiferayUser,
  getCountriesCatalog,
  createDrnowUser,
  checkEmail,
};

export default client;
